import {
  formatLocalDate,
  fromApiMoney,
  type Instant,
  type LocalDate,
  type Money,
  parseInstant,
  parseLocalDate,
  toApiMoney,
} from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import type { Page } from './page.ts'

export type ReconciliationStatus = 'OPEN' | 'FINISHED'
export type ResidualTarget = 'RECONCILIATION' | 'PNL'
/** Only ever set once a reconciliation is `FINISHED` - see `ReconciliationRowResponse`. */
export type ReconciliationOutcome = 'MATCHED' | 'CORRECTED' | 'TRANSFERRED' | 'REVERSED'

export interface ReconciliationRow {
  readonly accountId: string
  readonly computedBalance: Money
  /** Absent means no actual balance has been entered for this account in this session. */
  readonly observedBalance: Money | undefined
  readonly difference: Money | undefined
  readonly residualTarget: ResidualTarget
  /** Set when this account already carries a snapshot on this date - from outside this
   * session while open, or this session's own snapshot once finished. */
  readonly reconciledSnapshotId: string | undefined
  readonly updatedBy: string | undefined
  readonly updatedAt: Instant | undefined
  /** Present only on a finished reconciliation's own rows. */
  readonly outcome: ReconciliationOutcome | undefined
  readonly correctingEntryId: string | undefined
}

export interface ReconciliationProposal {
  readonly reallocations: readonly {
    fromAccountId: string
    toAccountId: string
    amount: Money
    entryId: string | undefined
  }[]
  readonly residuals: readonly { accountId: string; amount: Money; target: ResidualTarget }[]
  readonly matchedAccountIds: readonly string[]
}

export interface ReconciliationDetail {
  readonly id: string
  readonly observedDate: LocalDate
  readonly status: ReconciliationStatus
  readonly createdBy: string
  readonly createdAt: Instant
  readonly updatedAt: Instant
  readonly finishedBy: string | undefined
  readonly finishedAt: Instant | undefined
  readonly appliedReallocations: boolean | undefined
  readonly rows: readonly ReconciliationRow[]
  /** Present only while `status === 'OPEN'`. */
  readonly proposal: ReconciliationProposal | undefined
}

export interface ReconciliationSummary {
  readonly id: string
  readonly observedDate: LocalDate
  readonly status: ReconciliationStatus
  readonly accountsChecked: number
  readonly accountsCorrected: number
  readonly reallocationCount: number
  readonly createdBy: string
  readonly createdAt: Instant
  readonly finishedBy: string | undefined
  readonly finishedAt: Instant | undefined
}

export type ReconciliationListQuery = OperationQuery<'reconciliations_list'>

export interface FinishReconciliationInput {
  readonly applyReallocations: boolean
  readonly expected: readonly { accountId: string; computedBalance: Money }[]
}

export const reconciliationKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'reconciliations', 'list'),
  /** A distinct key from `list` even though both call the same operation - they run with
   * different query params (a cheap `limit: 1` presence check vs. the paged history), and
   * sharing one cache key would let whichever ran first clobber the other's result. */
  openCheck: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'reconciliations', 'open-check'),
  detail: (ledgerId: string, id: string): CacheKey => ledgerKey(ledgerId, 'reconciliations', 'detail', id),
  /** Finishing posts entries, so every account-money view this ledger has is stale too. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [
    ledgerKey(ledgerId, 'reconciliations'),
    ledgerKey(ledgerId, 'snapshots'),
    ledgerKey(ledgerId, 'reporting'),
    ledgerKey(ledgerId, 'entries'),
    ledgerKey(ledgerId, 'accounts'),
  ],
} as const

export interface ReconciliationsRepository {
  create(ledgerId: string, observedDate: LocalDate): Promise<ReconciliationDetail>
  list(
    ledgerId: string,
    query?: ReconciliationListQuery,
    signal?: AbortSignal,
  ): Promise<Page<ReconciliationSummary>>
  get(ledgerId: string, id: string, signal?: AbortSignal): Promise<ReconciliationDetail>
  update(ledgerId: string, id: string, observedDate: LocalDate): Promise<ReconciliationDetail>
  setLine(
    ledgerId: string,
    id: string,
    accountId: string,
    observedBalance: Money,
  ): Promise<ReconciliationDetail>
  clearLine(ledgerId: string, id: string, accountId: string): Promise<void>
  finish(ledgerId: string, id: string, input: FinishReconciliationInput): Promise<ReconciliationDetail>
  abandon(ledgerId: string, id: string): Promise<void>
}

export function createReconciliationsRepository(client: TalerClient): ReconciliationsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/reconciliations`
  const one = (ledgerId: string, id: string) => `${base(ledgerId)}/${encodeURIComponent(id)}`
  const line = (ledgerId: string, id: string, accountId: string) =>
    `${one(ledgerId, id)}/lines/${encodeURIComponent(accountId)}`

  return {
    async create(ledgerId, observedDate) {
      return toDetail(
        await client.call<Schemas['ReconciliationDetailResponse'], Schemas['CreateReconciliationRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: { observedDate: formatLocalDate(observedDate) },
        }),
      )
    },

    async list(ledgerId, query, signal) {
      const page = await client.call<Schemas['PageResponseReconciliationSummaryResponse']>({
        method: 'GET',
        path: base(ledgerId),
        query: {
          ...(query?.limit === undefined ? {} : { limit: String(query.limit) }),
          ...(query?.cursor === undefined ? {} : { cursor: query.cursor }),
        },
        ...(signal === undefined ? {} : { signal }),
      })
      return { items: (page.items ?? []).map(toSummary), nextCursor: page.nextCursor }
    },

    async get(ledgerId, id, signal) {
      return toDetail(
        await client.call<Schemas['ReconciliationDetailResponse']>({
          method: 'GET',
          path: one(ledgerId, id),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async update(ledgerId, id, observedDate) {
      return toDetail(
        await client.call<Schemas['ReconciliationDetailResponse'], Schemas['UpdateReconciliationRequest']>({
          method: 'PATCH',
          path: one(ledgerId, id),
          body: { observedDate: formatLocalDate(observedDate) },
        }),
      )
    },

    async setLine(ledgerId, id, accountId, observedBalance) {
      const path = line(ledgerId, id, accountId)
      const body: Schemas['SetReconciliationLineRequest'] = { observedBalance: apiMoney(observedBalance) }
      const response = await client.call<Schemas['ReconciliationDetailResponse'], typeof body>({
        method: 'PUT',
        path,
        body,
      })
      // Typing 100, then 1000, then back to 100 must not replay the first save's response.
      client.idempotencyKeys.forget({ method: 'PUT', path, query: null, body })
      return toDetail(response)
    },

    async clearLine(ledgerId, id, accountId) {
      await client.call<undefined>({ method: 'DELETE', path: line(ledgerId, id, accountId) })
    },

    async finish(ledgerId, id, input) {
      return toDetail(
        await client.call<Schemas['ReconciliationDetailResponse'], Schemas['FinishReconciliationRequest']>({
          method: 'POST',
          path: `${one(ledgerId, id)}/finish`,
          body: {
            applyReallocations: input.applyReallocations,
            expected: input.expected.map((row) => ({
              accountId: row.accountId,
              computedBalance: apiMoney(row.computedBalance),
            })),
          },
        }),
      )
    },

    async abandon(ledgerId, id) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, id) })
    },
  }
}

/** See `loans.ts`'s identical helper: `@taler/domain`'s currency list is wider than the wire enum. */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

function toRow(row: Schemas['ReconciliationRowResponse']): ReconciliationRow {
  return {
    accountId: row.accountId,
    computedBalance: fromApiMoney(row.computedBalance),
    observedBalance: row.observedBalance === undefined ? undefined : fromApiMoney(row.observedBalance),
    difference: row.difference === undefined ? undefined : fromApiMoney(row.difference),
    residualTarget: row.residualTarget as ResidualTarget,
    reconciledSnapshotId: row.reconciledSnapshotId,
    updatedBy: row.updatedBy,
    updatedAt: row.updatedAt === undefined ? undefined : parseInstant(row.updatedAt),
    outcome: row.outcome === undefined ? undefined : (row.outcome as ReconciliationOutcome),
    correctingEntryId: row.correctingEntryId,
  }
}

function toProposal(
  proposal: Schemas['ReconciliationProposalResponse'] | undefined,
): ReconciliationProposal | undefined {
  if (proposal === undefined) return undefined
  return {
    reallocations: proposal.reallocations.map((entry) => ({
      fromAccountId: entry.fromAccountId,
      toAccountId: entry.toAccountId,
      amount: fromApiMoney(entry.amount),
      entryId: entry.entryId,
    })),
    residuals: proposal.residuals.map((entry) => ({
      accountId: entry.accountId,
      amount: fromApiMoney(entry.amount),
      target: entry.target as ResidualTarget,
    })),
    matchedAccountIds: proposal.matchedAccountIds,
  }
}

function toDetail(response: Schemas['ReconciliationDetailResponse']): ReconciliationDetail {
  return {
    id: response.id,
    observedDate: parseLocalDate(response.observedDate),
    status: response.status as ReconciliationStatus,
    createdBy: response.createdBy,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
    finishedBy: response.finishedBy,
    finishedAt: response.finishedAt === undefined ? undefined : parseInstant(response.finishedAt),
    appliedReallocations: response.appliedReallocations,
    rows: response.rows.map(toRow),
    proposal: toProposal(response.proposal),
  }
}

function toSummary(response: Schemas['ReconciliationSummaryResponse']): ReconciliationSummary {
  return {
    id: response.id,
    observedDate: parseLocalDate(response.observedDate),
    status: response.status as ReconciliationStatus,
    accountsChecked: response.accountsChecked,
    accountsCorrected: response.accountsCorrected,
    reallocationCount: response.reallocationCount,
    createdBy: response.createdBy,
    createdAt: parseInstant(response.createdAt),
    finishedBy: response.finishedBy,
    finishedAt: response.finishedAt === undefined ? undefined : parseInstant(response.finishedAt),
  }
}
